import React, {Component} from 'react';
import {isEmpty, getToken, getRefreshToken} from '../../../../../../utils/helpers';
import moment from "moment";
import {showModal} from "../../../../../../actions/modal.actions";
import {connect} from "react-redux";
import {getUserPermission} from "../../../../../../selectors";
import DiagnosticIndicator, { DiagnosticStatus, mapStatus } from "src/components/UI/DiagnosticIndicator";
import {API_ROOT} from "../../../../../../utils/api-config";

export class ServiceSummary extends Component {

    state = {
        diagnostics: {},
        loadingDiagnostics: {},
        deviceStatuses: {},
        deviceLogs: {},
    }

    fetchDiagnostics = async (deviceProfileId) => {
        this.setState(prevState => ({
            loadingDiagnostics: {
                ...prevState.loadingDiagnostics,
                [deviceProfileId]: true
            }
        }));

        try {
            const response = await fetch(`${API_ROOT}/provisioning/diagnostics?deviceProfileId=${deviceProfileId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'X-API-Token': getToken(),
                    'X-API-Refresh-Token': getRefreshToken()
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const diagnosticData = await response.json();

            this.setState(prevState => ({
                diagnostics: {
                    ...prevState.diagnostics,
                    [deviceProfileId]: diagnosticData
                },
                loadingDiagnostics: {
                    ...prevState.loadingDiagnostics,
                    [deviceProfileId]: false
                }
            }));

            this.autoRunDeviceStatus(deviceProfileId, diagnosticData);

        } catch (error) {
            this.setState(prevState => ({
                loadingDiagnostics: {
                    ...prevState.loadingDiagnostics,
                    [deviceProfileId]: false
                }
            }));
        }
    }

    autoRunDeviceStatus = async (deviceProfileId, diagnosticData) => {
        const deviceStatusOperation = diagnosticData?.find(diag => diag.operationName === 'device-status');

        if (!deviceStatusOperation) {
            return;
        }

        const {service} = this.props;
        const device = service?.serviceDevices?.find(d => d.deviceProfileId === deviceProfileId);

        if (!device) {
            return;
        }

        await this.runDiagnosticAction('device-status', device, deviceStatusOperation);
    }

    runDiagnosticAction = async (actionKey, device, diagnosticResponse) => {
        const deviceId = device.id || `${device.deviceProfileId}-${device.serialNumber}`;

        try {
            this.setState(prevState => ({
                deviceStatuses: {
                    ...prevState.deviceStatuses,
                    [deviceId]: DiagnosticStatus.LOADING
                }
            }));

            const payload = {
                deviceNums: device?.apiServiceDeviceNums || [],
                deviceProfile: diagnosticResponse?.deviceProfileName || "ONT",
                interfaces: [diagnosticResponse?.provisioningInterfaceName || "CALIX-SMX"]
            };

            const response = await fetch(
                `${API_ROOT}/provisioning/diagnostic/${actionKey}`,
                {
                    method: "POST",
                    headers: {
                        'Content-Type': 'application/json',
                        'X-API-Token': getToken(),
                        'X-API-Refresh-Token': getRefreshToken()
                    },
                    body: JSON.stringify(payload)
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const actionResponseData = await response.json();

            if (actionResponseData && actionResponseData.state) {
                const newStatus = mapStatus(actionResponseData);

                let parsedLogs = actionResponseData.rawResponse;
                if (typeof parsedLogs === 'string') {
                    try {
                        const jsonString = parsedLogs.replace(/'/g, '"').replace(/None/g, 'null').replace(/True/g, 'true').replace(/False/g, 'false');
                        parsedLogs = JSON.parse(jsonString);
                    } catch (parseError) {
                        console.warn('Failed to parse rawResponse string:', parseError);
                        parsedLogs = { error: 'Failed to parse response', raw: parsedLogs };
                    }
                }

                this.setState(prevState => ({
                    deviceStatuses: {
                        ...prevState.deviceStatuses,
                        [deviceId]: newStatus
                    },
                    deviceLogs: {
                        ...prevState.deviceLogs,
                        [deviceId]: parsedLogs
                    }
                }));
            } else {
                this.setState(prevState => ({
                    deviceStatuses: {
                        ...prevState.deviceStatuses,
                        [deviceId]: DiagnosticStatus.UNKNOWN
                    }
                }));
            }

        } catch (error) {
            this.setState(prevState => ({
                deviceStatuses: {
                    ...prevState.deviceStatuses,
                    [deviceId]: DiagnosticStatus.UNKNOWN
                }
            }));
        }
    }

    showLogsModal = (device, deviceId) => {
        const deviceLogs = this.state.deviceLogs[deviceId];
        const deviceStatus = this.state.deviceStatuses[deviceId];
        const deviceProfileId = device.deviceProfileId;
        const diagnosticResponse = this.state.diagnostics[deviceProfileId];
        const deviceStatusDiagnostic = diagnosticResponse?.find(diag => diag.operationName === 'device-status');

        this.props.showModal('EQUIPMENT_DEVICE_STATUS_LOGS_MODAL', {
            device: device,
            logs: deviceLogs,
            status: deviceStatus,
            diagnosticResponse: deviceStatusDiagnostic,
            diagnostics: diagnosticResponse,
            deviceId: deviceId,
            onUpdateParent: (updatedStatus, updatedLogs) => {
                this.setState(prevState => ({
                    deviceStatuses: {
                        ...prevState.deviceStatuses,
                        [deviceId]: updatedStatus
                    },
                    deviceLogs: {
                        ...prevState.deviceLogs,
                        [deviceId]: updatedLogs
                    }
                }));
            }
        });
    }

    componentDidMount() {
        const {service} = this.props;

        if (service && service.serviceDevices) {
            service.serviceDevices.forEach(device => {
                if (device.deviceProfileId) {
                    this.fetchDiagnostics(device.deviceProfileId);
                }
            });
        }
    }

    componentDidUpdate(prevProps) {
        const {service} = this.props;

        if (service && service.serviceDevices) {
            const prevDevices = prevProps.service?.serviceDevices || [];
            const currentDevices = service.serviceDevices || [];

            currentDevices.forEach(device => {
                if (device.deviceProfileId) {
                    const prevDevice = prevDevices.find(d => d.deviceProfileId === device.deviceProfileId);
                    if (!prevDevice) {
                        this.fetchDiagnostics(device.deviceProfileId);
                    }
                }
            });
        }
    }

    render() {

        const {diagnostics, loadingDiagnostics, deviceStatuses, deviceLogs} = this.state;

        const {service, canEditEquipment} = this.props;

        if (isEmpty(service)) {
            return null;
        }

        let recurringFeaturePackageFees = null;
        let recurringFeatureFees = null;
        let oneTimeFeaturePackageFees = null;
        let oneTimeFeatureFees = null;

        recurringFeaturePackageFees = !isEmpty(service.activeFeaturePackages) ?
            service.activeFeaturePackages.filter((x) => x.featureType !== 'ACTIVATION') : null;

        recurringFeatureFees = !isEmpty(service.servicelineFeatures) ?
            service.servicelineFeatures.filter((x) => x.featureType !== 'ACTIVATION') : null;

        oneTimeFeaturePackageFees = !isEmpty(service.activeFeaturePackages) ?
            service.activeFeaturePackages.filter((x) => x.featureType === 'ACTIVATION') : null;

        oneTimeFeatureFees = !isEmpty(service.servicelineFeatures) ?
            service.servicelineFeatures.filter((x) => x.featureType === 'ACTIVATION') : null;

        return (
            <div className="services-summary mt-0">
                <div className="services-summary-body">
                    <div className="plan-wrapper">
                        <div className="serviceline-wrapper">

                            {service.servicelineNums &&
                            <div className="plan-section">
                                <h5>Numbers</h5>
                                {!isEmpty(service.servicelineNums) && service.servicelineNums.map((num) =>
                                    <div className="package-wrapper">
                                        <div className="package-option">
                                            <span className="po-title">{num.number}</span>
                                            <span className="po-price">{num.type}</span>
                                            {service.endDate &&
                                            <div className="po-date">
                                                {num.startDate && moment(num.startDate).format("MM/DD/YYYY")}
                                                {" - "}
                                                {num.endDate ? moment(num.endDate).format("MM/DD/YYYY") : "Now"}
                                            </div>
                                            }
                                        </div>
                                    </div>
                                )}
                            </div>
                            }

                            {!isEmpty(recurringFeaturePackageFees) || !isEmpty(recurringFeatureFees) ? (
                                <div className="plan-section">

                                    <h5>Recurring</h5>

                                    {!isEmpty(recurringFeaturePackageFees) &&
                                        recurringFeaturePackageFees.map((feature, index) => (
                                            <div className="package-wrapper" key={index}>
                                                <div className="package-option">
                                                    <span className="po-title">
                                                        {feature.description && `${feature.description.replace(/\s<br><(\w+)\b[^<>]*>[\s\S]*?<\/\1>/g, '')}:`}{' '}
                                                        {feature.featurePackageOptions.map((featureAndPackageOptions) => {
                                                            return featureAndPackageOptions.description
                                                        })}
                                                    </span>
                                                    <span className="po-price">
                                                        ${feature.featurePackageOptions[0].recurringMonthlyPrice
                                                            .toFixed(2)
                                                            .replace(/\d(?=(\d{3})+\.)/g, '$&,')}/mo
                                                    </span>
                                                </div>
                                            </div>
                                        )
                                    )}

                                    {!isEmpty(recurringFeatureFees) &&
                                        recurringFeatureFees.map((feature, index) => (
                                            <div className="package-wrapper" key={index}>
                                                <div className="package-option">
                                                    <span className="po-title">
                                                        {feature.featureDescription && feature.featureDescription}
                                                    </span>
                                                    <span className="po-price">
                                                        {feature.qty > 1
                                                            ? <>Qty: {feature.qty}<span className="text-muted"> | </span></>
                                                            : ""}
                                                        ${feature.recurringMonthlyPrice
                                                            .toFixed(2)
                                                            .replace(/\d(?=(\d{3})+\.)/g, '$&,')}/mo
                                                    </span>
                                                    {service.endDate &&
                                                    <div className="po-date">
                                                        {feature.startDate && moment(feature.startDate).format("MM/DD/YYYY")}
                                                        {" - "}
                                                        {feature.endDate ? moment(feature.endDate).format("MM/DD/YYYY") : "Now"}
                                                    </div>
                                                    }
                                                </div>
                                            </div>
                                        )
                                    )}

                                </div>
                            ): null}

                            {!isEmpty(oneTimeFeaturePackageFees) || !isEmpty(oneTimeFeatureFees) ? (
                            <div className="plan-section">

                                <h5>Onetime</h5>

                                {!isEmpty(oneTimeFeaturePackageFees) &&
                                    oneTimeFeaturePackageFees .map((feature, index) => (
                                        <div className="package-wrapper" key={index}>
                                            <div className="package-option package-option-mandatory">
                                                <span className="po-title">
                                                    {feature.description && `${feature.description}:`}{' '}
                                                    {feature.note}
                                                </span>
                                                <span className="po-price">
                                                    ${feature.featurePackageOptions[0].oneTimePrice
                                                        .toFixed(2)
                                                        .replace(/\d(?=(\d{3})+\.)/g, '$&,')}
                                                </span>
                                            </div>
                                        </div>
                                    )
                                )}

                                {!isEmpty(oneTimeFeatureFees) &&
                                    oneTimeFeatureFees.map((feature, index) => (
                                        <div className="package-wrapper" key={index}>
                                            <div className="package-option package-option-mandatory">
                                                <span className="po-title">
                                                    {feature.featureDescription && feature.featureDescription}
                                                </span>
                                                <span className="po-price">
                                                    {feature.qty > 1
                                                        ? <>Qty: {feature.qty}<span className="text-muted"> | </span></>
                                                        : ""}
                                                    ${feature.oneTimePrice
                                                        .toFixed(2)
                                                        .replace(/\d(?=(\d{3})+\.)/g, '$&,')}
                                                </span>
                                            </div>
                                            {service.endDate &&
                                            <div className="po-date">
                                                {feature.startDate && moment(feature.startDate).format("MM/DD/YYYY")}
                                                {" - "}
                                                {feature.endDate ? moment(feature.endDate).format("MM/DD/YYYY") : "Now"}
                                            </div>
                                            }
                                        </div>
                                    )
                                )}

                            </div>
                            ) : null }

                            {!isEmpty(service.serviceDevices) &&
                            <div className="plan-section">

                                <h5>Equipment</h5>

                                {service.serviceDevices.map((device, index) => {
                                    const deviceProfileId = device.deviceProfileId;
                                    const diagnosticResponse = diagnostics[deviceProfileId];
                                    const isLoading = loadingDiagnostics[deviceProfileId];
                                    const hasDeviceDiagnostics = diagnosticResponse && Array.isArray(diagnosticResponse) && diagnosticResponse.length > 0;

                                    const availableDiagnostics = hasDeviceDiagnostics ? diagnosticResponse : null;

                                    const deviceStatusDiagnostic = hasDeviceDiagnostics
                                        ? diagnosticResponse.find(diag => diag.operationName === 'device-status')
                                        : null;

                                    return (
                                <div className="package-option" key={index}>
                                    <span className="po-title">{device.deviceItemDescription ? device.deviceItemDescription : device.deviceProfile}</span>
                                    {canEditEquipment &&
                                        <button
                                            title="Edit"
                                            onClick={() =>
                                                this.props.showModal('EDIT_EQUIPMENT_MODAL', {
                                                    device: device,
                                                    service: service,
                                                    showHistoricalData: false
                                                })
                                            }
                                            className="btn btn-xs btn-outline-secondary btn-cn-edit"
                                        >
                                            Edit
                                        </button>
                                    }
                                    {service.endDate &&
                                    <div className="po-date">
                                        {device.startDate && moment(device.startDate).format("MM/DD/YYYY")}
                                        {" - "}
                                        {device.endDate ? moment(device.endDate).format("MM/DD/YYYY") : "Now"}
                                    </div>
                                    }
                                    <div className="container-list-bubble container-list-bubble-equipment" style={{ position: "relative" }}>
                                        {hasDeviceDiagnostics && (() => {
                                            const deviceId = device.id || `${device.deviceProfileId}-${device.serialNumber}`;
                                            const deviceStatus = deviceStatuses[deviceId] || DiagnosticStatus.UNKNOWN;
                                            const deviceLogsData = deviceLogs[deviceId];
                                            const hasLogs = !!deviceLogsData;

                                            return (
                                                <DiagnosticIndicator
                                                    diagnosticResponse={availableDiagnostics}
                                                    statusDiagnostic={deviceStatusDiagnostic}
                                                    isLoading={isLoading}
                                                    onRunAction={this.runDiagnosticAction}
                                                    onToggleLogs={() => this.showLogsModal(device, deviceId)}
                                                    device={device}
                                                    status={deviceStatus}
                                                    logs={deviceLogsData}
                                                    hasLogs={hasLogs}
                                                    style={{
                                                        position: "absolute",
                                                        top: "8px",
                                                        right: "8px"
                                                    }}
                                                />
                                            );
                                        })()}

                                        {device.apiServiceDeviceNums && device.apiServiceDeviceNums.map((num, index) =>
                                          <dl key={index}>
                                              <dt>{num.type}</dt>
                                              <dd>{num.number}</dd>
                                          </dl>
                                        )}
                                    </div>
                                    </div>
                                    );
                                })}

                            </div>
                            }

                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

const mapStateToProps = (state) => {
    const canEditEquipment = getUserPermission(state, 'SERVICE_LINES', 'SERVICE_LINE_EDIT_EQUIPMENT');
    return {
        canEditEquipment,
    };
};

const mapDispatchToProps = {
    showModal,
};

export default connect(mapStateToProps, mapDispatchToProps)(ServiceSummary);
